/**
 * App.js
 */
import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Switch, Route, withRouter, Redirect} from 'react-router-dom';
import {hot} from 'react-hot-loader';
import styled from 'styled-components';

import Async from '@ey/async';

import Env from '../util/env';
import {routes as pageRoutes} from '../util/uiconstants';
import {getAdminRoutes} from '../components/AdminPortal/Common/util';

import Projects from './Pages/Projects';
import CreateProjectClassic from './Pages/CreateProjectClassic';
import CreateProjectGRA from './Pages/CreateProjectGRA';
import Dashboard from './Pages/Dashboard';
import GroupRiskAssessment from './Pages/GroupRiskAssessment';
import ContactDB from './Pages/ContactDB';
import UserSettings from './Pages/UserSettings';

import WizardsDrydock from './Pages/WizardsDrydock';

import Main from './Main/Main';
import LoaderContainer from './LoaderContainer';
import BreadCrumbNavigation from '../components/Common/Nav/BreadCrumbNavigation';
import {adobeAnalytics} from '@ey/voltron-analytics';
import envConfig from '@ey/voltron-config';
import {pageViewArgs} from '../components/AdobeAnalytics/actionHelpers';
import {pageNames, siteSections} from '../components/AdobeAnalytics/constants';

const searchParams = new URLSearchParams(location.search);

const adobeAnalyticsEnabled = envConfig.adobeAnalytics?.enabled;

/* the import() call must be located in this outer function. Placing it
 * inside load() will cause Webpack to raise a warning about a required
 * file path being an expression.
 * The value of the component={} attribute must be a function or a
 * React component. React-Router sends the props in as the first parameter,
 * so grab those and forward them to the Async.load() as the first
 * argument. The second argument must be an import() statement. */
const drydockRoutes = [{path: '/drydock', component: WizardsDrydock}];

const appRoutes = [
	{
		path: `/${pageRoutes.project.root}`,
		component: adobeAnalytics.TrackPageView(Projects, pageViewArgs(pageNames.myActiveProjects, siteSections.home))
	},
	{
		path: `/${pageRoutes.project.root}/${pageRoutes.project.classic}`,
		component: adobeAnalytics.TrackPageView(
			CreateProjectClassic,
			pageViewArgs(pageNames.createProject, siteSections.projects)
		)
	},
	{
		path: `/${pageRoutes.project.root}/${pageRoutes.project.gra}`,
		component: adobeAnalytics.TrackPageView(CreateProjectGRA, pageViewArgs(pageNames.createGra, siteSections.projects))
	},
	{
		path: `${pageRoutes.usersettings.root}`,
		component: adobeAnalytics.TrackPageView(
			UserSettings,
			pageViewArgs(pageNames.userSettings, siteSections.userAccount)
		)
	},
	{
		path: `/${pageRoutes.dashboard.root}`,
		component: adobeAnalytics.TrackPageView(Dashboard, pageViewArgs(pageNames.dashboard, siteSections.project))
	},
	{
		path: `/${pageRoutes.contacts.root}`,
		component: adobeAnalytics.TrackPageView(ContactDB, pageViewArgs(pageNames.contacts, siteSections.contacts))
	},
	{
		path: `/${pageRoutes.gra.root}`,
		component: adobeAnalytics.TrackPageView(GroupRiskAssessment, pageViewArgs(pageNames.gra, siteSections.gra))
	},
	{
		path: '',
		component: adobeAnalytics.TrackPageView(Projects, pageViewArgs(pageNames.myActiveProjects, siteSections.home, true))
	} // default
];

const redirectRoutes = [
	{
		path: `/${pageRoutes.usersettings.root}`,
		component: adobeAnalytics.TrackPageView(
			UserSettings,
			pageViewArgs(pageNames.userSettings, siteSections.userAccount)
		)
	}
];

let routes = [];
if (Env.allowDrydocks()) routes = [...drydockRoutes, ...appRoutes];
else routes = [...appRoutes];

const App = (props) => {
	const dispatch = useDispatch();
	const userPreferences = useSelector((store) => store.userPreferences);
	const paansUserData = useSelector((store) => store.paansuserdata);

	const adminRoutes = getAdminRoutes();
	const shouldRedirect = () => {
		if (
			!(
				userPreferences.isPreferencesLoaded &&
				userPreferences.userId &&
				userPreferences.userId !== '00000000-0000-0000-0000-000000000000' &&
				userPreferences.languageISOCode
			)
		) {
			return true; //USER FIRST TIME
		} else {
			return false; //USER NOT FIRST TIME
		}
	};
	let path = props.location?.pathname;

	// Adobe Analytics
	useEffect(() => {
		if (adobeAnalyticsEnabled && userPreferences?.emailAddress) {
			dispatch(
				adobeAnalytics.initAdobeAnalytics(
					userPreferences.emailAddress,
					userPreferences.isEYUser,
					envConfig.adobeAnalytics.defaultDataLayer
				)
			);
		}
	}, [userPreferences]);

	const getAdminAppContainer = () => {
		return (
			<>
				{userPreferences.isPreferencesLoaded ? (
					<Main>
						<Switch>
							{adminRoutes.map((eachRoute, index) => {
								const {path, component, breadCrumb, moduleName, iconName} = eachRoute;
								return (
									<Route path={path} key={index}>
										<BreadCrumbSection className={`breadcrumb-wrap ${_.isEmpty(breadCrumb) && 'hide-bread-crumb'}`}>
											<section className="mainResponsiveWrapper">
												<BreadCrumbNavigation breadCrumbList={breadCrumb} />
											</section>
										</BreadCrumbSection>
										{component}
									</Route>
								);
							})}
						</Switch>
					</Main>
				) : (
					<LoaderContainer path={props.location} />
				)}
			</>
		);
	};

	const verifyAdminToolUrl = (path) => {
		path = path !== null ? path.split('/') : null;
		if (path !== null && path.length > 1 && path[1].toLowerCase() === 'admin') {
			return true;
		}
		return false;
	};

	const getOversightAppContainer = () => {
		return (
			<>
				{userPreferences.isPreferencesLoaded ? (
					<Main>
						<Switch>
							{redirectRoutes.map((eachRoute, index) => {
								const {path, component} = eachRoute;
								return <Route exact key={index} path={path} component={component} />;
							})}
							{shouldRedirect() ? (
								<Route>
									<Redirect to="/usersettings" />
								</Route>
							) : null}

							{(paansUserData.isPaansEnabled == false || paansUserData.hasAccepted == true) &&
								routes.map((eachRoute, index) => {
									const {path, component} = eachRoute;
									return <Route exact key={index} path={path} component={component} />;
								})}
						</Switch>
					</Main>
				) : (
					<LoaderContainer path={props.location} />
				)}
			</>
		);
	};

	return <>{verifyAdminToolUrl(path) ? getAdminAppContainer() : getOversightAppContainer()}</>;
};

export default hot(module)(withRouter(App));

const BreadCrumbSection = styled.section`
	position: fixed;
	width: 100%;
	z-index: 99;
	background: ${(props) => props.theme.color[`white`]};
	box-shadow: 0.1rem 0.1rem 1rem ${(props) => props.theme.color[`grey300black`]};
	height: 4.8rem;

	&.hide-bread-crumb {
		display: none;
	}
`;
